import { createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import Mdx from "#/components/MdxRemote";
import { metadataToHead, type PageMetadata } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <header>
        <NavBar />
      </header>

      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <article>
          <h1 className="mb-10 px-5 text-center text-5xl">
            Doctor Salary in the United States
          </h1>

          <section className="mx-8 space-y-5">
            <p>
              The average medical doctor salary in the United States is{" "}
              <strong>$210,000</strong> and the range is usually between{" "}
              <strong>$170,000</strong> and <strong>$280,000</strong>.
            </p>

            <p>
              Physicians are in high demand across the United States. This not
              only makes their career fulfilling, but also a financially
              rewarding choice for many.
            </p>
          </section>

          <section className="mx-8 space-y-5">
            <h2>How much doctors make by specialty</h2>

            <Mdx source={content} />
          </section>
        </article>
      </main>
      <Footer fmBio />
    </>
  );
}

export const metadata: PageMetadata = {
  title: `Salary for Doctors in the United States`,
  description:
    "The average doctor salary in the United States is $210,000 and the range is usually between $170,000 and $280,000.",
  openGraph: {
    images: ["https://roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/doctor-salary-in-united-states`,
  },
};

const content = `
The income of doctors in the United States can vary significantly depending on their specialty. While all medical fields are generally well-compensated, some specialties tend to have higher average salaries due to factors like the level of expertise required, the demand for the specialty, the length of training, and the intensity of the work involved. Below, we explore some of these specialties to give you an idea of how much doctors make, based on recent data.

### Family Medicine
Family medicine physicians provide comprehensive health care for individuals and families across all ages, genders, diseases, and parts of the body. They are known for their holistic approach to medicine and ability to build long-term patient relationships. On average, family medicine doctors earn between $200,000 and $230,000 per year.

### Pediatrics
Pediatricians specialize in the physical, emotional, and social health of children from infancy to young adulthood. This specialty is crucial for early detection of health problems, guidance on nutrition and physical fitness, and managing acute and chronic illnesses. Pediatricians typically earn between $180,000 and $220,000 annually.

### Cardiology
Cardiologists specialize in diagnosing and treating diseases of the cardiovascular system. The heart and blood vessels are critical to the overall health of individuals, and cardiologists play a key role in helping patients manage heart disease, the leading cause of death in the U.S. Cardiologists' salaries range significantly, but they typically earn between $350,000 and $500,000 per year, with interventional cardiologists often at the higher end of this range.

### Orthopedics
Orthopedic surgeons deal with the musculoskeletal system, including bones, joints, ligaments, tendons, and muscles. They are involved in the diagnosis, treatment, and rehabilitation of various conditions and injuries. Orthopedic surgeons are among the highest-paid physicians, with average annual salaries ranging from $400,000 to $600,000, depending on their subspecialty and practice setting.

### Neurology
Neurologists specialize in the nervous system and deal with disorders that affect the brain, spinal cord, and nerves. They play a crucial role in treating conditions like epilepsy, stroke, multiple sclerosis, and Parkinson's disease. On average, neurologists earn between $250,000 and $350,000 per year.

### Psychiatry
Psychiatrists focus on the diagnosis, treatment, and prevention of mental, emotional, and behavioral disorders. They have a unique approach that combines the science of medicine with mental health therapy. The average annual salary for psychiatrists ranges from $220,000 to $300,000.

### Dermatology
Dermatologists specialize in the skin, the largest organ of the body, and also treat diseases of the hair, nails, and mucous membranes. Dermatology is known for offering a good work-life balance and a high level of patient satisfaction. Dermatologists typically earn between $300,000 and $400,000 per year.

### Anesthesiology
Anesthesiologists play a critical role in patient care by providing pain relief and maintaining vital life functions during surgery. They are involved before, during, and after surgical procedures, ensuring patients' safety and comfort. Anesthesiologists' salaries range from $350,000 to $450,000 annually.

`;

export const Route = createFileRoute("/(salary)/doctor-salary-in-united-states/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
